import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, isSameDay, startOfDay, endOfDay, addDays, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import QRCode from 'qrcode';

// Funciones de timezone para Calendario
// Las fechas se guardan en UTC en MongoDB. Siempre mostrar hora de Uruguay (America/Montevideo)
// sin importar dónde esté el usuario.
const formatDateTimeMontevideoCalendar = (dateTimeString) => {
  if (!dateTimeString) return null;
  
  try {
    let dateStr = typeof dateTimeString === 'string' ? dateTimeString : new Date(dateTimeString).toISOString();
    
    // Si no tiene timezone, tratar como UTC (MongoDB guarda en UTC)
    if (typeof dateStr === 'string' && !dateStr.includes('Z') && !dateStr.includes('+') && !/\d{2}-\d{2}:\d{2}$/.test(dateStr)) {
      dateStr += 'Z';
    }
    
    const utcDate = new Date(dateStr);
    if (isNaN(utcDate.getTime())) return null;
    
    // Convertir a hora de Uruguay forzada: creamos un Date cuyo valor "local"
    // refleje la hora de Montevideo, para que format() de date-fns lo muestre correcto
    const mvdString = utcDate.toLocaleString('en-US', { timeZone: 'America/Montevideo' });
    return new Date(mvdString);
  } catch {
    return null;
  }
};

const Calendario = () => {
  const { user, API } = useContext(AuthContext);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [clases, setClases] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [reuniones, setReuniones] = useState([]);
  const [pruebas, setPruebas] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [citas, setCitas] = useState([]);
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today'); // today, week, month
  
  // Estados para modal de actividades
  const [showActividadModal, setShowActividadModal] = useState(false);
  const [actividadEditando, setActividadEditando] = useState(null);
  const [actividadForm, setActividadForm] = useState({
    titulo: '',
    descripcion: '',
    fecha_inicio: '',
    hora_inicio: '09:00',
    fecha_fin: '',
    hora_fin: '12:00',
    ubicacion: '',
    tipo: 'actividad',
    destinatarios: 'todos',
    estudiantes_seleccionados: []
  });
  const [savingActividad, setSavingActividad] = useState(false);
  
  // Estados para modal de detalle/QR de actividad
  const [showActividadDetailModal, setShowActividadDetailModal] = useState(false);
  const [actividadDetalle, setActividadDetalle] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [showAsistenciaModal, setShowAsistenciaModal] = useState(false);

  // Colores por tipo
  const COLORES = {
    clase: { bg: 'blue', icon: 'fas fa-chalkboard-teacher', label: 'Clase' },
    capacitacion: { bg: 'cyan', icon: 'fas fa-graduation-cap', label: 'Capacitación' },
    tarea: { bg: 'green', icon: 'fas fa-tasks', label: 'Tarea' },
    evento: { bg: 'purple', icon: 'fas fa-calendar-plus', label: 'Evento' },
    prueba: { bg: 'red', icon: 'fas fa-file-alt', label: 'Prueba/Examen' },
    reunion: { bg: 'orange', icon: 'fas fa-users', label: 'Reunión' },
    formulario: { bg: 'amber', icon: 'fas fa-clipboard-list', label: 'Formulario' },
    actividad: { bg: 'emerald', icon: 'fas fa-star', label: 'Actividad Extra' },
    cita: { bg: 'indigo', icon: 'fas fa-calendar-check', label: 'Solicitud Reunión' }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const requests = [
        axios.get(`${API}/clases`, { headers }),
        axios.get(`${API}/tareas`, { headers }),
        axios.get(`${API}/eventos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/formularios/calendario`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/pruebas-examenes`, { headers }).catch(() => ({ data: { pruebas: [] } })),
        axios.get(`${API}/actividades-extracurriculares`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/citas`, { headers }).catch(() => ({ data: { citas: [] } })),
      ];

      // Agregar reuniones de profesores si es profesor o admin
      if (user?.rol === 'profesor' || user?.rol === 'admin') {
        requests.push(axios.get(`${API}/convocatorias-reunion`, { headers }).catch(() => ({ data: { convocatorias: [] } })));
      }
      
      // Cargar lista de estudiantes para admin/profesor (para selección en actividades)
      if (user?.rol === 'admin' || user?.rol === 'profesor') {
        requests.push(axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: { users: [] } })));
      }

      const results = await Promise.all(requests);
      
      // Filtrar clases activas según el rol
      let clasesData = results[0].data || [];
      clasesData = clasesData.filter(clase => {
        // Solo clases activas
        if (clase.estado !== 'activo' && clase.activa !== true) return false;
        
        // Estudiantes: solo clases de estudiantes (no capacitaciones)
        if (user?.rol === 'estudiante' || user?.rol === 'pastor') {
          return clase.categoria === 'estudiantes' || clase.tipo !== 'capacitacion';
        }
        // Profesores: clases que dictan + clases a las que asisten + capacitaciones
        if (user?.rol === 'profesor') {
          // Ver sus propias clases (las que dicta)
          if (clase.profesor_id === user?.id) return true;
          // Ver clases a las que está inscrito
          if (clase.estudiantes_inscritos?.includes(user?.id)) return true;
          // Ver capacitaciones para profesores
          if (clase.tipo === 'capacitacion' || clase.categoria === 'profesores') return true;
          return false;
        }
        // Admin: todas
        return true;
      });
      
      setClases(clasesData);
      setTareas(results[1].data || []);
      setEventos(results[2].data || []);
      setFormularios(results[3].data || []);
      setPruebas(results[4].data?.pruebas || results[4].data || []);
      setActividades(results[5].data || []);
      // Citas 1-a-1 (sólo aceptadas/pendientes y futuras)
      const citasRaw = results[6]?.data?.citas || [];
      setCitas(citasRaw.filter(c => c.estado === 'aceptada' || c.estado === 'pendiente'));
      
      if (user?.rol === 'profesor' || user?.rol === 'admin') {
        setReuniones(results[7]?.data?.convocatorias || []);
        // Filtrar solo estudiantes activos para la selección
        const usersData = results[8]?.data?.users || results[8]?.data || [];
        const estudiantesActivos = usersData.filter(u => 
          ['estudiante', 'pastor', 'graduado'].includes(u.rol) && u.estado === 'activo'
        );
        setEstudiantes(estudiantesActivos);
      }
    } catch (error) {
      toast.error('Error al cargar datos del calendario');
    } finally {
      setLoading(false);
    }
  };

  const getEventsForDate = (date) => {
    const events = [];
    
    // Add classes (con recurrencia semanal)
    clases.forEach(clase => {
      const claseStartDate = formatDateTimeMontevideoCalendar(clase.fecha_inicio);
      const claseEndDate = formatDateTimeMontevideoCalendar(clase.fecha_fin);
      
      // Ignorar clases sin fechas válidas
      if (!claseStartDate || !claseEndDate) return;
      
      // Verificar si la fecha está en el rango de la clase
      const dateToCheck = startOfDay(date);
      const classStart = startOfDay(claseStartDate);
      const classEnd = startOfDay(claseEndDate);
      
      if (dateToCheck >= classStart && dateToCheck <= classEnd) {
        // Verificar si el día de la semana coincide
        const diasSemana = clase.dias_semana || [];
        const dayOfWeek = date.getDay();
        
        const dayNames = {
          'domingo': 0, 'Domingo': 0,
          'lunes': 1, 'Lunes': 1,
          'martes': 2, 'Martes': 2,
          'miércoles': 3, 'Miércoles': 3, 'miercoles': 3, 'Miercoles': 3,
          'jueves': 4, 'Jueves': 4,
          'viernes': 5, 'Viernes': 5,
          'sábado': 6, 'Sábado': 6, 'sabado': 6, 'Sabado': 6
        };
        
        const classDays = diasSemana.map(dia => dayNames[dia] || -1).filter(d => d !== -1);
        
        if (classDays.includes(dayOfWeek)) {
          const esCapacitacion = clase.tipo === 'capacitacion';
          const colorConfig = esCapacitacion ? COLORES.capacitacion : COLORES.clase;
          
          events.push({
            id: clase.id,
            type: esCapacitacion ? 'capacitacion' : 'clase',
            title: clase.titulo,
            time: format(claseStartDate, 'HH:mm', { locale: es }),
            endTime: format(claseEndDate, 'HH:mm', { locale: es }),
            profesor: clase.profesor_nombre,
            zoomLink: clase.zoom_link,
            color: colorConfig.bg,
            icon: colorConfig.icon
          });
        }
      }
    });
    
    // Add task deadlines
    tareas.forEach(tarea => {
      if (!tarea.fecha_limite) return; // Ignorar tareas sin fecha
      const tareaDate = formatDateTimeMontevideoCalendar(tarea.fecha_limite);
      if (!tareaDate) return;
      if (isSameDay(tareaDate, date)) {
        events.push({
          id: tarea.id,
          type: 'tarea',
          title: tarea.titulo,
          time: format(tareaDate, 'HH:mm', { locale: es }),
          description: tarea.descripcion,
          color: COLORES.tarea.bg,
          icon: COLORES.tarea.icon
        });
      }
    });
    
    // Add eventos
    eventos.forEach(evento => {
      // Solo eventos activos
      if (evento.estado !== 'activo') return;
      
      const eventoStartDate = formatDateTimeMontevideoCalendar(evento.fecha_inicio);
      const eventoEndDate = formatDateTimeMontevideoCalendar(evento.fecha_fin);
      
      // Ignorar eventos sin fechas válidas
      if (!eventoStartDate) return;
      
      if (isSameDay(eventoStartDate, date) || 
          (eventoEndDate && eventoStartDate <= startOfDay(date) && eventoEndDate >= endOfDay(date))) {
        events.push({
          id: evento.id,
          type: 'evento',
          title: evento.titulo,
          time: format(eventoStartDate, 'HH:mm', { locale: es }),
          endTime: format(eventoEndDate, 'HH:mm', { locale: es }),
          description: evento.descripcion,
          ubicacion: evento.ubicacion,
          zoomLink: evento.link_zoom,
          tipo: evento.tipo,
          estado: evento.estado,
          color: COLORES.evento.bg,
          icon: evento.tipo === 'online' ? 'fas fa-video' : COLORES.evento.icon
        });
      }
    });
    
    // Add pruebas y examenes
    pruebas.forEach(prueba => {
      // Solo activas Y con fecha válida
      if (prueba.estado !== 'activo' && prueba.activa !== true) return;
      if (!prueba.fecha) return; // Ignorar pruebas sin fecha
      
      const pruebaDate = formatDateTimeMontevideoCalendar(prueba.fecha);
      if (isSameDay(pruebaDate, date)) {
        events.push({
          id: prueba.id,
          type: 'prueba',
          title: prueba.titulo,
          time: format(pruebaDate, 'HH:mm', { locale: es }),
          description: prueba.descripcion,
          materia: prueba.materia_nombre,
          color: COLORES.prueba.bg,
          icon: COLORES.prueba.icon
        });
      }
    });
    
    // Add reuniones de profesores (solo para profesor y admin)
    if (user?.rol === 'profesor' || user?.rol === 'admin') {
      reuniones.forEach(reunion => {
        if (!reunion.fecha_reunion) return; // Ignorar reuniones sin fecha
        const reunionDate = formatDateTimeMontevideoCalendar(reunion.fecha_reunion);
        if (!reunionDate) return;
        if (isSameDay(reunionDate, date)) {
          events.push({
            id: reunion.id,
            type: 'reunion',
            title: reunion.titulo,
            time: format(reunionDate, 'HH:mm', { locale: es }),
            description: reunion.descripcion,
            zoomLink: reunion.link_zoom,
            prioridad: reunion.prioridad,
            color: COLORES.reunion.bg,
            icon: COLORES.reunion.icon
          });
        }
      });
    }

    // Add citas 1-a-1 (Solicitud Reunión) — todas las que el usuario es solicitante o invitado
    citas.forEach(cita => {
      if (!cita.fecha_hora_inicio) return;
      const citaDate = formatDateTimeMontevideoCalendar(cita.fecha_hora_inicio);
      if (!citaDate) return;
      if (isSameDay(citaDate, date)) {
        const soySolicitante = cita.solicitante_id === user?.id;
        const otraPersona = soySolicitante ? cita.invitado_nombre : cita.solicitante_nombre;
        events.push({
          id: `cita-${cita.id}`,
          type: 'cita',
          title: `${cita.titulo || 'Reunión'} · ${otraPersona || ''}`.trim(),
          time: format(citaDate, 'HH:mm', { locale: es }),
          description: cita.motivo || (cita.estado === 'pendiente' ? '⏳ Pendiente de aprobación' : '✅ Confirmada'),
          dailyRoomUrl: cita.daily_room_url,
          estado: cita.estado,
          color: COLORES.cita.bg,
          icon: COLORES.cita.icon
        });
      }
    });
    
    // Add formularios deadlines (solo con fecha válida y no muy lejana)
    const maxFutureDate = new Date();
    maxFutureDate.setFullYear(maxFutureDate.getFullYear() + 1); // Máximo 1 año en el futuro
    
    formularios.forEach(formulario => {
      if (formulario.fecha_limite) {
        const formularioDeadline = formatDateTimeMontevideoCalendar(formulario.fecha_limite);
        
        // Ignorar fechas inválidas o muy lejanas (más de 1 año)
        if (!formularioDeadline || formularioDeadline > maxFutureDate) return;
        
        if (isSameDay(formularioDeadline, date)) {
          events.push({
            id: formulario.id,
            type: 'formulario',
            title: `📝 ${formulario.titulo}`,
            time: format(formularioDeadline, 'HH:mm', { locale: es }),
            description: formulario.descripcion || 'Formulario de evaluación',
            clase_titulo: formulario.clase_titulo,
            total_preguntas: formulario.total_preguntas || 0,
            color: COLORES.formulario.bg,
            icon: COLORES.formulario.icon
          });
        }
      }
    });
    
    // Add actividades extracurriculares
    actividades.forEach(actividad => {
      // Solo actividades activas
      if (actividad.estado !== 'activo') return;
      
      const actividadStartDate = formatDateTimeMontevideoCalendar(actividad.fecha_inicio);
      if (!actividadStartDate) return;
      
      if (isSameDay(actividadStartDate, date)) {
        const actividadEndDate = formatDateTimeMontevideoCalendar(actividad.fecha_fin);
        events.push({
          id: actividad.id,
          type: 'actividad',
          title: `⭐ ${actividad.titulo}`,
          time: format(actividadStartDate, 'HH:mm', { locale: es }),
          endTime: actividadEndDate ? format(actividadEndDate, 'HH:mm', { locale: es }) : null,
          description: actividad.descripcion,
          ubicacion: actividad.ubicacion,
          tipoActividad: actividad.tipo,
          creador_nombre: actividad.creador_nombre,
          asistencias: actividad.asistencias || [],
          rawData: actividad,
          color: COLORES.actividad.bg,
          icon: COLORES.actividad.icon
        });
      }
    });
    
    return events.sort((a, b) => a.time.localeCompare(b.time));
  };

  const hasEventsOnDate = (date) => {
    return getEventsForDate(date).length > 0;
  };

  const getDateRange = () => {
    const today = new Date();
    switch (view) {
      case 'week':
        const startOfWeek = subDays(today, today.getDay());
        const endOfWeek = addDays(startOfWeek, 6);
        return { start: startOfWeek, end: endOfWeek };
      case 'month':
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: startOfMonth, end: endOfMonth };
      default:
        return { start: today, end: today };
    }
  };

  // Función específica para "Próximos Eventos" - siempre muestra próximos 7 días
  const getUpcomingEventsRange = () => {
    const today = startOfDay(new Date());
    const nextWeek = addDays(today, 7);
    return { start: today, end: nextWeek };
  };

  const getEventsInRange = () => {
    const { start, end } = getDateRange();
    const events = [];
    
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dayEvents = getEventsForDate(currentDate);
      if (dayEvents.length > 0) {
        events.push({
          date: new Date(currentDate),
          events: dayEvents
        });
      }
      currentDate = addDays(currentDate, 1);
    }
    
    return events;
  };

  // Obtener eventos de los próximos 7 días (para widget "Próximos Eventos")
  const getUpcomingEvents = () => {
    const { start, end } = getUpcomingEventsRange();
    const events = [];
    
    let currentDate = new Date(start);
    while (currentDate <= end) {
      const dayEvents = getEventsForDate(currentDate);
      if (dayEvents.length > 0) {
        events.push({
          date: new Date(currentDate),
          events: dayEvents
        });
      }
      currentDate = addDays(currentDate, 1);
    }
    
    return events;
  };

  const formatDateHeader = (date) => {
    return format(date, "EEEE, d 'de' MMMM", { locale: es });
  };

  // =============== FUNCIONES PARA ACTIVIDADES EXTRACURRICULARES ===============
  
  const resetActividadForm = () => {
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    setActividadForm({
      titulo: '',
      descripcion: '',
      fecha_inicio: todayStr,
      hora_inicio: '09:00',
      fecha_fin: todayStr,
      hora_fin: '12:00',
      ubicacion: '',
      tipo: 'actividad',
      destinatarios: 'todos',
      estudiantes_seleccionados: []
    });
    setActividadEditando(null);
  };

  const openNuevaActividad = () => {
    resetActividadForm();
    setShowActividadModal(true);
  };

  const openEditarActividad = (actividad) => {
    const fechaInicio = formatDateTimeMontevideoCalendar(actividad.fecha_inicio) || new Date(actividad.fecha_inicio);
    const fechaFin = formatDateTimeMontevideoCalendar(actividad.fecha_fin) || new Date(actividad.fecha_fin);
    
    setActividadForm({
      titulo: actividad.titulo,
      descripcion: actividad.descripcion,
      fecha_inicio: format(fechaInicio, 'yyyy-MM-dd'),
      hora_inicio: format(fechaInicio, 'HH:mm'),
      fecha_fin: format(fechaFin, 'yyyy-MM-dd'),
      hora_fin: format(fechaFin, 'HH:mm'),
      ubicacion: actividad.ubicacion || '',
      tipo: actividad.tipo || 'actividad',
      destinatarios: actividad.destinatarios || 'todos',
      estudiantes_seleccionados: actividad.estudiantes_seleccionados || []
    });
    setActividadEditando(actividad);
    setShowActividadModal(true);
  };

  const handleGuardarActividad = async () => {
    if (!actividadForm.titulo.trim() || !actividadForm.descripcion.trim()) {
      toast.error('El título y la descripción son obligatorios');
      return;
    }
    
    if (!actividadForm.fecha_inicio || !actividadForm.fecha_fin) {
      toast.error('Las fechas son obligatorias');
      return;
    }

    setSavingActividad(true);
    
    try {
      const token = localStorage.getItem('token');
      const fechaInicioCompleta = `${actividadForm.fecha_inicio}T${actividadForm.hora_inicio}:00`;
      const fechaFinCompleta = `${actividadForm.fecha_fin}T${actividadForm.hora_fin}:00`;
      
      const payload = {
        titulo: actividadForm.titulo.trim(),
        descripcion: actividadForm.descripcion.trim(),
        fecha_inicio: fechaInicioCompleta,
        fecha_fin: fechaFinCompleta,
        ubicacion: actividadForm.ubicacion.trim() || null,
        tipo: actividadForm.tipo,
        destinatarios: actividadForm.destinatarios,
        estudiantes_seleccionados: actividadForm.destinatarios === 'seleccionados' 
          ? actividadForm.estudiantes_seleccionados 
          : []
      };

      if (actividadEditando) {
        // Actualizar
        await axios.put(
          `${API}/actividades-extracurriculares/${actividadEditando.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Actividad actualizada correctamente');
      } else {
        // Crear nueva
        await axios.post(
          `${API}/actividades-extracurriculares`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Actividad creada correctamente');
      }

      setShowActividadModal(false);
      resetActividadForm();
      loadData();
    } catch (error) {
      console.error('Error guardando actividad:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la actividad');
    } finally {
      setSavingActividad(false);
    }
  };

  const handleCancelarActividad = async (actividadId) => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar esta actividad?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/actividades-extracurriculares/${actividadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Actividad cancelada');
      loadData();
      setShowActividadDetailModal(false);
    } catch (error) {
      toast.error('Error al cancelar la actividad');
    }
  };

  const openActividadDetail = async (actividad) => {
    setActividadDetalle(actividad.rawData || actividad);
    setShowActividadDetailModal(true);
    
    // Generar QR Code con link completo
    try {
      const baseUrl = window.location.origin;
      const asistenciaUrl = `${baseUrl}/asistencia-actividad/${actividad.id}`;
      const qrUrl = await QRCode.toDataURL(asistenciaUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#166534', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('Error generando QR:', err);
    }
  };

  const handleMarcarAsistenciaManual = async (estudianteId, estudianteNombre) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/actividades-extracurriculares/${actividadDetalle.id}/marcar-asistencia-qr`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { estudiante_id: estudianteId }
        }
      );
      toast.success(`Asistencia registrada para ${estudianteNombre}`);
      loadData();
      
      // Actualizar el detalle
      const updatedActividad = { 
        ...actividadDetalle, 
        asistencias: [...(actividadDetalle.asistencias || []), { estudiante_id: estudianteId, nombre: estudianteNombre }]
      };
      setActividadDetalle(updatedActividad);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar asistencia');
    }
  };

  const toggleEstudianteSeleccionado = (estudianteId) => {
    setActividadForm(prev => {
      const yaSeleccionado = prev.estudiantes_seleccionados.includes(estudianteId);
      return {
        ...prev,
        estudiantes_seleccionados: yaSeleccionado
          ? prev.estudiantes_seleccionados.filter(id => id !== estudianteId)
          : [...prev.estudiantes_seleccionados, estudianteId]
      };
    });
  };

  // =============== FIN FUNCIONES ACTIVIDADES ===============

  const selectedEvents = getEventsForDate(selectedDate);
  const rangeEvents = getEventsInRange();
  const upcomingEvents = getUpcomingEvents(); // Siempre próximos 7 días

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-96 bg-gray-200 rounded-lg"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="calendario-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Calendario Académico</h1>
            <p className="text-gray-600">Visualiza clases, tareas, eventos y formularios de evaluación</p>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2 mt-4 lg:mt-0">
          {/* Botón Nueva Actividad - Solo admin/profesor */}
          {(user?.rol === 'admin' || user?.rol === 'profesor') && (
            <Button
              onClick={openNuevaActividad}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-nueva-actividad"
            >
              <i className="fas fa-star mr-2"></i>
              Nueva Actividad
            </Button>
          )}
          
          <Button
            variant={view === 'today' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('today')}
            className={view === 'today' ? 'instituto-gradient text-white' : ''}
            data-testid="view-today"
          >
            <i className="fas fa-calendar-day mr-2"></i>
            Hoy
          </Button>
          <Button
            variant={view === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('week')}
            className={view === 'week' ? 'instituto-gradient text-white' : ''}
            data-testid="view-week"
          >
            <i className="fas fa-calendar-week mr-2"></i>
            Semana
          </Button>
          <Button
            variant={view === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('month')}
            className={view === 'month' ? 'instituto-gradient text-white' : ''}
            data-testid="view-month"
          >
            <i className="fas fa-calendar-alt mr-2"></i>
            Mes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-xl">
                <i className="fas fa-calendar text-blue-600 mr-3"></i>
                {format(selectedDate, "MMMM yyyy", { locale: es }).charAt(0).toUpperCase() + format(selectedDate, "MMMM yyyy", { locale: es }).slice(1)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={es}
                className="rounded-md border shadow-sm"
                modifiers={{
                  hasEvents: (date) => hasEventsOnDate(date)
                }}
                modifiersStyles={{
                  hasEvents: {
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    fontWeight: 'bold'
                  }
                }}
                data-testid="calendar-widget"
              />
              
              {/* Legend */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Leyenda:</h4>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center">
                    <i className="fas fa-chalkboard-teacher text-blue-600 mr-1"></i>
                    <span>Clases</span>
                  </div>
                  {(user?.rol === 'profesor' || user?.rol === 'admin') && (
                    <div className="flex items-center">
                      <i className="fas fa-graduation-cap text-cyan-600 mr-1"></i>
                      <span>Capacitaciones</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <i className="fas fa-tasks text-green-600 mr-1"></i>
                    <span>Tareas</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-calendar-plus text-purple-600 mr-1"></i>
                    <span>Eventos</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-file-alt text-red-600 mr-1"></i>
                    <span>Pruebas</span>
                  </div>
                  <div className="flex items-center">
                    <i className="fas fa-star text-emerald-600 mr-1"></i>
                    <span>Actividades Extra</span>
                  </div>
                  {(user?.rol === 'profesor' || user?.rol === 'admin') && (
                    <div className="flex items-center">
                      <i className="fas fa-users text-orange-600 mr-1"></i>
                      <span>Reuniones</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Events Panel */}
        <div className="space-y-6">
          {/* Selected Date Events */}
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg">
                <i className="fas fa-calendar-day text-blue-600 mr-2"></i>
                {formatDateHeader(selectedDate).charAt(0).toUpperCase() + formatDateHeader(selectedDate).slice(1)}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {selectedEvents.length > 0 ? (
                <div className="space-y-3">
                  {selectedEvents.map((event) => (
                    <div 
                      key={`${event.type}-${event.id}`} 
                      className={`p-3 rounded-lg border-l-4 bg-gray-50 ${
                        event.color === 'blue' ? 'border-l-blue-500' :
                        event.color === 'cyan' ? 'border-l-cyan-500' :
                        event.color === 'green' ? 'border-l-green-500' :
                        event.color === 'purple' ? 'border-l-purple-500' :
                        event.color === 'red' ? 'border-l-red-500' :
                        event.color === 'orange' ? 'border-l-orange-500' :
                        event.color === 'amber' ? 'border-l-amber-500' :
                        event.color === 'emerald' ? 'border-l-emerald-500' :
                        'border-l-gray-500'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <i className={`${event.icon} mr-2 ${
                              event.color === 'blue' ? 'text-blue-600' :
                              event.color === 'cyan' ? 'text-cyan-600' :
                              event.color === 'green' ? 'text-green-600' :
                              event.color === 'purple' ? 'text-purple-600' :
                              event.color === 'red' ? 'text-red-600' :
                              event.color === 'orange' ? 'text-orange-600' :
                              event.color === 'amber' ? 'text-amber-600' :
                              event.color === 'emerald' ? 'text-emerald-600' :
                              'text-gray-600'
                            }`}></i>
                            <h4 className="font-semibold text-gray-900 text-sm">{event.title}</h4>
                            {event.estado === 'cancelado' && (
                              <Badge variant="secondary" className="ml-2 text-xs bg-red-100 text-red-800">
                                Cancelado
                              </Badge>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-600 mb-2">
                            <i className="fas fa-clock mr-1"></i>
                            {event.endTime ? `${event.time} - ${event.endTime}` : event.time}
                          </p>
                          
                          {event.profesor && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-user mr-1"></i>
                              Prof. {event.profesor}
                            </p>
                          )}
                          
                          {event.clase_titulo && event.type === 'formulario' && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-chalkboard mr-1"></i>
                              {event.clase_titulo}
                            </p>
                          )}
                          
                          {event.total_preguntas && event.type === 'formulario' && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-question-circle mr-1"></i>
                              {event.total_preguntas} pregunta{event.total_preguntas !== 1 ? 's' : ''}
                            </p>
                          )}
                          
                          {event.ubicacion && event.type === 'evento' && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-map-marker-alt mr-1"></i>
                              {event.ubicacion}
                            </p>
                          )}
                          
                          {/* Ubicación para actividades extracurriculares */}
                          {event.ubicacion && event.type === 'actividad' && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-map-marker-alt mr-1"></i>
                              {event.ubicacion}
                            </p>
                          )}
                          
                          {/* Creador de la actividad */}
                          {event.type === 'actividad' && event.creador_nombre && (
                            <p className="text-xs text-gray-600 mb-1">
                              <i className="fas fa-user-tie mr-1"></i>
                              Creado por: {event.creador_nombre}
                            </p>
                          )}
                          
                          {event.description && (
                            <p className="text-xs text-gray-500 line-clamp-2">
                              {event.description}
                            </p>
                          )}
                        </div>
                        
                        <div className="ml-2 flex flex-col space-y-1">
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              event.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                              event.color === 'cyan' ? 'bg-cyan-100 text-cyan-800' :
                              event.color === 'green' ? 'bg-green-100 text-green-800' :
                              event.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                              event.color === 'red' ? 'bg-red-100 text-red-800' :
                              event.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                              event.color === 'amber' ? 'bg-amber-100 text-amber-800' :
                              event.color === 'emerald' ? 'bg-emerald-100 text-emerald-800' :
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {event.type === 'clase' ? 'Clase' : 
                             event.type === 'capacitacion' ? 'Capacitación' :
                             event.type === 'tarea' ? 'Tarea' : 
                             event.type === 'formulario' ? 'Formulario' :
                             event.type === 'prueba' ? 'Prueba' :
                             event.type === 'reunion' ? 'Reunión' :
                             event.type === 'actividad' ? 'Actividad' :
                             'Evento'}
                          </Badge>
                          
                          {/* Botón Zoom directo: NO mostrar en clases/capacitaciones
                              porque saltea el flujo de marcar asistencia.
                              Los estudiantes deben entrar por Área Académica → registrar asistencia. */}
                          {event.zoomLink && event.type !== 'clase' && event.type !== 'capacitacion' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={() => window.open(event.zoomLink, '_blank')}
                              data-testid={`btn-zoom-${event.type}-${event.id}`}
                            >
                              <i className="fas fa-video"></i>
                            </Button>
                          )}
                          
                          {/* Botón Ver Detalles para actividades */}
                          {event.type === 'actividad' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-emerald-600 hover:text-emerald-700"
                              onClick={() => openActividadDetail(event)}
                              data-testid={`btn-ver-actividad-${event.id}`}
                            >
                              <i className="fas fa-eye mr-1"></i>
                              Ver
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="fas fa-calendar text-gray-300 text-3xl mb-3"></i>
                  <p className="text-gray-500 text-sm">No hay eventos para esta fecha</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events - Siempre muestra próximos 7 días */}
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">
                <i className="fas fa-clock text-green-600 mr-2"></i>
                Próximos Eventos
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {upcomingEvents.length > 0 ? (
                <div className="space-y-4">
                  {upcomingEvents.slice(0, 5).map((dayData, index) => (
                    <div key={index}>
                      <div className="flex items-center mb-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {format(dayData.date, "EEEE d MMM", { locale: es })}
                        </h4>
                      </div>
                      <div className="ml-4 space-y-2">
                        {dayData.events.slice(0, 3).map((event) => (
                          <div key={`${event.type}-${event.id}`} className="text-xs text-gray-600 flex items-center">
                            <span className={`w-2 h-2 rounded-full mr-2 ${
                              event.type === 'clase' ? 'bg-blue-500' :
                              event.type === 'tarea' ? 'bg-orange-500' :
                              event.type === 'evento' ? 'bg-green-500' :
                              event.type === 'reunion' ? 'bg-purple-500' :
                              'bg-gray-500'
                            }`}></span>
                            <span className="font-medium">{event.time}</span> - {event.title}
                          </div>
                        ))}
                        {dayData.events.length > 3 && (
                          <div className="text-xs text-blue-600">
                            +{dayData.events.length - 3} más...
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="fas fa-calendar-check text-gray-300 text-3xl mb-3"></i>
                  <p className="text-gray-500 text-sm">No hay eventos en los próximos 7 días</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Quick Stats */}
      <div className={`grid grid-cols-1 gap-6 ${(user?.rol === 'profesor' || user?.rol === 'admin') ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-chalkboard-teacher text-blue-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {clases.length}
            </h3>
            <p className="text-gray-600">{user?.rol === 'profesor' ? 'Capacitaciones' : 'Clases'} Activas</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-tasks text-green-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {tareas.length}
            </h3>
            <p className="text-gray-600">Tareas</p>
          </CardContent>
        </Card>
        
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-calendar-plus text-purple-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {eventos.filter(evento => evento.estado === 'activo').length}
            </h3>
            <p className="text-gray-600">Eventos</p>
          </CardContent>
        </Card>

        {(user?.rol === 'profesor' || user?.rol === 'admin') && (
          <Card className="shadow-md border-0">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-users text-orange-600 text-xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {reuniones.length}
              </h3>
              <p className="text-gray-600">Reuniones</p>
            </CardContent>
          </Card>
        )}
        
        {/* Tarjeta de actividades extracurriculares */}
        <Card className="shadow-md border-0">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-star text-emerald-600 text-xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900">
              {actividades.filter(a => a.estado === 'activo').length}
            </h3>
            <p className="text-gray-600">Actividades Extra</p>
          </CardContent>
        </Card>
      </div>

      {/* =================== MODAL CREAR/EDITAR ACTIVIDAD =================== */}
      <Dialog open={showActividadModal} onOpenChange={setShowActividadModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-emerald-700">
              <i className="fas fa-star mr-2"></i>
              {actividadEditando ? 'Editar Actividad' : 'Nueva Actividad Extracurricular'}
            </DialogTitle>
            <DialogDescription>
              Crea actividades como viajes, talleres o eventos especiales para los estudiantes
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="titulo">Título de la actividad *</Label>
              <Input
                id="titulo"
                value={actividadForm.titulo}
                onChange={(e) => setActividadForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ej: Viaje a Colonia del Sacramento"
                data-testid="input-titulo-actividad"
              />
            </div>

            {/* Descripción */}
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción *</Label>
              <Textarea
                id="descripcion"
                value={actividadForm.descripcion}
                onChange={(e) => setActividadForm(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Describe los detalles de la actividad..."
                rows={3}
                data-testid="input-descripcion-actividad"
              />
            </div>

            {/* Tipo de actividad */}
            <div className="space-y-2">
              <Label>Tipo de actividad</Label>
              <Select
                value={actividadForm.tipo}
                onValueChange={(value) => setActividadForm(prev => ({ ...prev, tipo: value }))}
              >
                <SelectTrigger data-testid="select-tipo-actividad">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="actividad">Actividad General</SelectItem>
                  <SelectItem value="viaje">Viaje</SelectItem>
                  <SelectItem value="taller">Taller</SelectItem>
                  <SelectItem value="retiro">Retiro Espiritual</SelectItem>
                  <SelectItem value="conferencia">Conferencia</SelectItem>
                  <SelectItem value="visita">Visita</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Fechas y Horas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_inicio">Fecha inicio *</Label>
                <Input
                  id="fecha_inicio"
                  type="date"
                  value={actividadForm.fecha_inicio}
                  onChange={(e) => setActividadForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                  data-testid="input-fecha-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_inicio">Hora inicio</Label>
                <Input
                  id="hora_inicio"
                  type="time"
                  value={actividadForm.hora_inicio}
                  onChange={(e) => setActividadForm(prev => ({ ...prev, hora_inicio: e.target.value }))}
                  data-testid="input-hora-inicio"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_fin">Fecha fin *</Label>
                <Input
                  id="fecha_fin"
                  type="date"
                  value={actividadForm.fecha_fin}
                  onChange={(e) => setActividadForm(prev => ({ ...prev, fecha_fin: e.target.value }))}
                  data-testid="input-fecha-fin"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_fin">Hora fin</Label>
                <Input
                  id="hora_fin"
                  type="time"
                  value={actividadForm.hora_fin}
                  onChange={(e) => setActividadForm(prev => ({ ...prev, hora_fin: e.target.value }))}
                  data-testid="input-hora-fin"
                />
              </div>
            </div>

            {/* Ubicación */}
            <div className="space-y-2">
              <Label htmlFor="ubicacion">Ubicación</Label>
              <Input
                id="ubicacion"
                value={actividadForm.ubicacion}
                onChange={(e) => setActividadForm(prev => ({ ...prev, ubicacion: e.target.value }))}
                placeholder="Ej: Plaza Independencia, Montevideo"
                data-testid="input-ubicacion"
              />
            </div>

            {/* Destinatarios */}
            <div className="space-y-2">
              <Label>Destinatarios</Label>
              <Select
                value={actividadForm.destinatarios}
                onValueChange={(value) => setActividadForm(prev => ({ ...prev, destinatarios: value }))}
              >
                <SelectTrigger data-testid="select-destinatarios">
                  <SelectValue placeholder="Seleccionar destinatarios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los estudiantes</SelectItem>
                  <SelectItem value="seleccionados">Estudiantes seleccionados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Selector de estudiantes (si es seleccionados) */}
            {actividadForm.destinatarios === 'seleccionados' && (
              <div className="space-y-2">
                <Label>Seleccionar estudiantes ({actividadForm.estudiantes_seleccionados.length} seleccionados)</Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-3 space-y-2">
                  {estudiantes.length > 0 ? (
                    estudiantes.map((est) => (
                      <div key={est.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`est-${est.id}`}
                          checked={actividadForm.estudiantes_seleccionados.includes(est.id)}
                          onCheckedChange={() => toggleEstudianteSeleccionado(est.id)}
                        />
                        <label htmlFor={`est-${est.id}`} className="text-sm cursor-pointer">
                          {est.nombre} {est.apellido} <span className="text-gray-400">({est.email})</span>
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">Cargando estudiantes...</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActividadModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarActividad} 
              disabled={savingActividad}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-guardar-actividad"
            >
              {savingActividad ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  {actividadEditando ? 'Actualizar' : 'Crear Actividad'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =================== MODAL DETALLE DE ACTIVIDAD (con QR) =================== */}
      <Dialog open={showActividadDetailModal} onOpenChange={setShowActividadDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center text-emerald-700">
              <i className="fas fa-star mr-2"></i>
              {actividadDetalle?.titulo}
            </DialogTitle>
          </DialogHeader>

          {actividadDetalle && (
            <div className="space-y-4 py-4">
              {/* Info básica */}
              <div className="bg-emerald-50 p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  <i className="fas fa-info-circle text-emerald-600 mr-2"></i>
                  <strong>Tipo:</strong> {actividadDetalle.tipo?.charAt(0).toUpperCase() + actividadDetalle.tipo?.slice(1)}
                </p>
                <p className="text-sm">
                  <i className="fas fa-calendar text-emerald-600 mr-2"></i>
                  <strong>Fecha:</strong> {format(formatDateTimeMontevideoCalendar(actividadDetalle.fecha_inicio) || new Date(actividadDetalle.fecha_inicio), "dd/MM/yyyy HH:mm", { locale: es })}
                </p>
                {actividadDetalle.ubicacion && (
                  <p className="text-sm">
                    <i className="fas fa-map-marker-alt text-emerald-600 mr-2"></i>
                    <strong>Ubicación:</strong> {actividadDetalle.ubicacion}
                  </p>
                )}
                <p className="text-sm">
                  <i className="fas fa-user-tie text-emerald-600 mr-2"></i>
                  <strong>Creado por:</strong> {actividadDetalle.creador_nombre}
                </p>
              </div>

              {/* Descripción */}
              <div>
                <h4 className="font-medium mb-1">Descripción</h4>
                <p className="text-sm text-gray-600">{actividadDetalle.descripcion}</p>
              </div>

              {/* QR Code para asistencia (solo admin/profesor) */}
              {(user?.rol === 'admin' || user?.rol === 'profesor') && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <i className="fas fa-qrcode text-emerald-600 mr-2"></i>
                    Código QR para Asistencia
                  </h4>
                  <div className="flex justify-center">
                    {qrCodeUrl && (
                      <div className="text-center">
                        <a 
                          href={`/asistencia-actividad/${actividadDetalle.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block cursor-pointer hover:opacity-90 transition-opacity"
                          title="Toca para marcar tu asistencia"
                        >
                          <img 
                            src={qrCodeUrl} 
                            alt="QR Asistencia" 
                            className="mx-auto border-2 border-emerald-300 rounded-lg shadow hover:shadow-lg hover:border-emerald-500 transition-all" 
                          />
                        </a>
                        <p className="text-xs text-gray-500 mt-2">
                          <strong>Escanea</strong> el QR o <strong>toca</strong> para marcar asistencia
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lista de asistencias */}
              {(user?.rol === 'admin' || user?.rol === 'profesor') && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <i className="fas fa-clipboard-check text-emerald-600 mr-2"></i>
                    Asistencia ({actividadDetalle.asistencias?.length || 0})
                  </h4>
                  {actividadDetalle.asistencias?.length > 0 ? (
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {actividadDetalle.asistencias.map((a, idx) => (
                        <div key={idx} className="flex items-center text-sm bg-gray-50 p-2 rounded">
                          <i className="fas fa-check-circle text-green-500 mr-2"></i>
                          {a.nombre || a.estudiante_nombre}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Aún no hay asistencias registradas</p>
                  )}
                </div>
              )}

              {/* Botones de acción (solo admin/profesor) */}
              {(user?.rol === 'admin' || user?.rol === 'profesor') && (
                <div className="flex justify-end space-x-2 border-t pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowActividadDetailModal(false);
                      openEditarActividad(actividadDetalle);
                    }}
                  >
                    <i className="fas fa-edit mr-2"></i>
                    Editar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleCancelarActividad(actividadDetalle.id)}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Cancelar Actividad
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Calendario;